import { useNavigate } from 'react-router-dom';
import { useOfflineItineraries } from '@/hooks/useOfflineItineraries';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, WifiOff, ExternalLink, ArrowLeft } from 'lucide-react';

const DownloadedPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { items, loading, remove } = useOfflineItineraries();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mr-1">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <WifiOff className="h-6 w-6 text-gray-500" />
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('offline.title')}
          </h1>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <WifiOff className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg font-medium">{t('offline.emptyTitle')}</p>
            <p className="text-sm mt-1">{t('offline.emptyDesc')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {entry.coverImage ? (
                  <img
                    src={entry.coverImage}
                    alt={entry.title}
                    className="w-full h-36 object-cover"
                  />
                ) : (
                  <div className="w-full h-36 bg-gray-100 flex items-center justify-center">
                    <WifiOff className="h-8 w-8 text-gray-300" />
                  </div>
                )}
                <div className="p-4">
                  <p className="font-semibold text-gray-900 truncate">{entry.title}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('offline.downloadedOn')}{' '}
                    {new Date(entry.downloadedAt).toLocaleDateString()}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/downloaded/${entry.id}`)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('offline.open')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => remove(entry.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadedPage;
